import React, { useEffect, useRef, useState } from "react";
import MainContainer from "../../layouts/container/mainContainer";
import Footer from "../../layouts/footer";
import { useTranslation } from "react-i18next";
import Button from "../../components/buttons/button";
import { FaFileDownload } from "react-icons/fa";
import {
  callApi,
  getReportActiveEmployers,
  getReportSelectedEmployers,
  getReportTravelers,
  getReportHanover,
  getReportFilters,
  getCurrentPrograms,
  getReportByProgram,
} from "../../services/apiService";
import { reportEnum, RoleEnum } from "../../constants/enum";
import { getUserInfo, isRole } from "../../services/authService";
import {
  downloadButton,
  downloadButtonLabel,
  filterLabel,
  filterRow,
  filterSection,
  filterSelect,
  filtersContainer,
  hanoverDropdownActions,
  hanoverDropdownContainer,
  hanoverDropdownOptions,
  hanoverDropdownPanel,
  hanoverOptionLabel,
  hanoverSelectButtonStyle,
  hanoverTrigger,
  hanoverTriggerIcon,
  hanoverTriggerLabel,
  pageTitle,
  reportSelect,
} from "./reports.styles";

const Reports = () => {
  const { t } = useTranslation();
  const isAdmin = isRole(RoleEnum.admin);
  var userInfo = getUserInfo();

  const [selectedReport, setSelectedReport] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [selectedActiveProgram, setSelectedActiveProgram] = useState("");
  const [activePrograms, setActivePrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState("");
  const [selectedHanoverPrograms, setSelectedHanoverPrograms] = useState([]);
  const [currentPrograms, setCurrentPrograms] = useState([]);
  const [hanoverDropdownOpen, setHanoverDropdownOpen] = useState(false);
  const hanoverDropdownRef = useRef(null);

  const sanitizeFileName = (name) => {
    return name
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") 
      .replace(/[^a-zA-Z0-9-_ ]/g, "") 
      .trim()
      .replace(/\s+/g, "_");
  };

  const handleSelectChange = (event) => {
    setSelectedReport(event.target.value);
    setSelectedHanoverPrograms([]);
    setHanoverDropdownOpen(false);
  };

  const toggleHanoverProgram = (programId) => {
    setSelectedHanoverPrograms((prev) =>
      prev.includes(programId)
        ? prev.filter((id) => id !== programId)
        : [...prev, programId]
    );
  };

  const selectAllHanoverPrograms = () => {
    setSelectedHanoverPrograms(currentPrograms.map((p) => String(p.id)));
  };

  const deselectAllHanoverPrograms = () => {
    setSelectedHanoverPrograms([]);
  };

  const getHanoverSelectLabel = () => {
    if (selectedHanoverPrograms.length === 0) {
      return t("report.choose");
    }
    if (selectedHanoverPrograms.length === 1) {
      const program = currentPrograms.find(
        (p) => String(p.id) === selectedHanoverPrograms[0]
      );
      return program?.name || t("report.choose");
    }
    return t("report.programsSelected", {
      count: selectedHanoverPrograms.length,
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        hanoverDropdownRef.current &&
        !hanoverDropdownRef.current.contains(event.target)
      ) {
        setHanoverDropdownOpen(false);
      }
    };

    if (hanoverDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [hanoverDropdownOpen]);

  useEffect(() => {
    callApi(
      () => getReportFilters(),
      (data) => {
        setPrograms(data.courses);
        setActivePrograms(data.activeCourses);
      }
    );
  }, [selectedReport]);

  useEffect(() => {
    if (selectedReport != reportEnum.hanover) {
      return;
    }
    callApi(
      () => getCurrentPrograms(),
      (data) => setCurrentPrograms(data)
    );
  }, [selectedReport]);

  const handleDownload = async () => {
    if (selectedReport == reportEnum.selectedEmployers) {
      callApi(
        () => getReportSelectedEmployers(),
        (data) => {
          const currentDate = new Date();
          const year = currentDate.getFullYear();
          const month = String(currentDate.getMonth() + 1).padStart(2, "0");
          const day = String(currentDate.getDate()).padStart(2, "0");
          const fileName = `selectedEmployers_${year}-${month}-${day}.xlsx`;
          downloadFileASync(data, fileName);
        }
      );
      return;
    }
    if (selectedReport == reportEnum.employersActived) {
      callApi(
        () => getReportActiveEmployers(),
        (data) => {
          const currentDate = new Date();
          const year = currentDate.getFullYear();
          const month = String(currentDate.getMonth() + 1).padStart(2, "0");
          const day = String(currentDate.getDate()).padStart(2, "0");
          const fileName = `activeEmployers_${year}-${month}-${day}.xlsx`;
          downloadFileASync(data, fileName);
        }
      );
      return;
    }
    if (selectedReport == reportEnum.travelers) {
      var body = {
        programId: selectedProgram,
        agencyUserId: isAdmin ? null : userInfo.id,
      };
      callApi(
        () => getReportTravelers(body),
        (data) => {
          const currentDate = new Date();
          const year = currentDate.getFullYear();
          const month = String(currentDate.getMonth() + 1).padStart(2, "0");
          const day = String(currentDate.getDate()).padStart(2, "0");
          const fileName = `travelersReport_${year}-${month}-${day}.xlsx`;
          downloadFileASync(data, fileName);
        }
      );
      return;
    }
    if (selectedReport == reportEnum.byProgram) {
      const body = {
        programId: selectedActiveProgram,
      };

      callApi(
        () => getReportByProgram(body),
        (data) => {
          const currentDate = new Date();
          const year = currentDate.getFullYear();
          const month = String(currentDate.getMonth() + 1).padStart(2, "0");
          const day = String(currentDate.getDate()).padStart(2, "0");

          const selectedProgramObj = activePrograms.find(
            (p) => p.id === selectedActiveProgram
          );

          const programName = selectedProgramObj?.name
            ? sanitizeFileName(selectedProgramObj.name)
            : "program";

          const fileName = `reportByProgram_${programName}_${year}-${month}-${day}.xlsx`;

          downloadFileASync(data, fileName);
        }
      );
      return;
    }
    if (selectedReport == reportEnum.hanover) {
      const body = {
        programIds: selectedHanoverPrograms,
      };
      callApi(
        () => getReportHanover(body),
        (data) => {
          const currentDate = new Date();
          const year = currentDate.getFullYear();
          const month = String(currentDate.getMonth() + 1).padStart(2, "0");
          const day = String(currentDate.getDate()).padStart(2, "0");
          const fileName = `hanoverReport_${year}-${month}-${day}.csv`;
          downloadCsvFile(data, fileName);
        }
      );
      return;
    }
    return;
  };

  const downloadCsvFile = (data, fileName) => {
    try {
      const base64String = data.file;
      const binary = atob(base64String);
      const byteArray = Uint8Array.from(binary, (char) => char.charCodeAt(0));
      const blob = new Blob([byteArray], {
        type: "text/csv;charset=utf-8;",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading the report:", error);
    }
  };

  const downloadFileASync = (data, fileName) => {
    try {
      const base64String = data.file;
      const byteCharacters = atob(base64String);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading the report:", error);
    }
  };

  return (
    <>
      <MainContainer className="ww-container-xxl" showImage={false}>
        <h1 style={pageTitle} className="title">
          {t("report.title")}
        </h1>
        <div style={filtersContainer}>
          <label htmlFor="reportSelect">{t("report.label")}</label>
          <select
            id="reportSelect"
            value={selectedReport}
            onChange={handleSelectChange}
            style={reportSelect}
          >
            <option value="">{t("report.choose")}</option>
            <option value={reportEnum.selectedEmployers}>
              {t("report.employersSelected")}
            </option>
            <option value={reportEnum.employersActived}>
              {t("report.employersActived")}
            </option>
            <option value={reportEnum.travelers}>
              {t("report.travelers")}
            </option>
            <option value={reportEnum.byProgram}>
              {t("report.byProgram")}
            </option>
            <option value={reportEnum.hanover}>{t("report.hanover")}</option>
          </select>

          {selectedReport == reportEnum.travelers && (
            <div style={filterSection}>
              <div style={filterRow}>
                <label style={filterLabel}>{t("report.program")}</label>
                <select
                  value={selectedProgram}
                  onChange={(e) => setSelectedProgram(e.target.value)}
                  style={filterSelect}
                >
                  <option value="">{t("report.choose")}</option>
                  {programs.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {selectedReport == reportEnum.hanover && (
            <div style={filterSection}>
              <div style={filterRow}>
                <label style={filterLabel}>{t("report.program")}</label>
                <div ref={hanoverDropdownRef} style={hanoverDropdownContainer}>
                  <button
                    type="button"
                    onClick={() => setHanoverDropdownOpen((open) => !open)}
                    style={hanoverTrigger}
                  >
                    <span style={hanoverTriggerLabel}>
                      {getHanoverSelectLabel()}
                    </span>
                    <span style={hanoverTriggerIcon}>
                      {hanoverDropdownOpen ? "▲" : "▼"}
                    </span>
                  </button>
                  {hanoverDropdownOpen && (
                    <div style={hanoverDropdownPanel}>
                      <div style={hanoverDropdownActions}>
                        <Button
                          style={hanoverSelectButtonStyle}
                          type="ww-btn-secondary"
                          size="btn-s secondary"
                          label={t("report.selectAll")}
                          onClick={selectAllHanoverPrograms}
                        />
                        <Button
                          style={hanoverSelectButtonStyle}
                          type="ww-btn-secondary"
                          size="btn-s secondary"
                          label={t("report.deselectAll")}
                          onClick={deselectAllHanoverPrograms}
                        />
                      </div>
                      <div style={hanoverDropdownOptions}>
                        {currentPrograms.map((a) => {
                          const programId = String(a.id);
                          return (
                            <label key={a.id} style={hanoverOptionLabel}>
                              <input
                                type="checkbox"
                                checked={selectedHanoverPrograms.includes(
                                  programId
                                )}
                                onChange={() => toggleHanoverProgram(programId)}
                              />
                              {a.name}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {selectedReport == reportEnum.byProgram && (
            <div style={filterSection}>
              <div style={filterRow}>
                <label style={filterLabel}>{t("report.program")}</label>
                <select
                  value={selectedActiveProgram}
                  onChange={(e) => setSelectedActiveProgram(e.target.value)}
                  style={filterSelect}
                >
                  <option value="">{t("report.choose")}</option>
                  {activePrograms.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>{" "}
        <Button
          label={
            <span style={downloadButtonLabel}>
              <FaFileDownload />
              Download
            </span>
          }
          onClick={handleDownload}
          disabled={
            !selectedReport ||
            (selectedReport == reportEnum.byProgram &&
              selectedActiveProgram == "") ||
            (selectedReport == reportEnum.hanover &&
              selectedHanoverPrograms.length === 0)
          }
          tooltip={t("postulation.title")}
          style={downloadButton}
        />
      </MainContainer>
      <Footer />
    </>
  );
};

export default Reports;
